import { useLayoutEffect, useState } from 'react';
import { MediaAttachmentsProps } from 'app/components/MediaAttachments';
import { useIsMediaLibraryEnabled } from 'app/hooks/useIsMediaLibraryEnabled';
import { useImageEditor } from 'app/components/imageEditor/hooks/useImageEditor';
import {
  AttachmentConvertor,
  mediaLibraryItemToAssetCard,
} from 'utils/attachments';
import {
  AssetAttachmentCard,
  AttachmentCard,
  AttachmentCardMedium,
  AttachmentCards,
} from 'types/Attachments';
import ArrayUtils from 'app/utils/array';
import { MediaLibraryItem, MediaLibraryItemFileType } from 'types/MediaLibrary';
import { useFileSource } from 'app/components/fileSourceInput/hooks/useFileSource';

type Props = Omit<MediaAttachmentsProps, 'allowedFiles'> & {
  allowedFiles: MediaLibraryItemFileType[];
  maxCount: number;
};

export function useMediaAttachmentsContainer({
  attachments,
  onAttachmentsChange,
  page,
  maxCount,
  allowedFiles,
  sources,
}: Props) {
  // The uploader owns runningUploads, so its count is only known after the
  // hook call; mirror it so the next render caps device uploads by it too.
  // A layout effect (not a passive one) so the corrected limit is committed
  // before the browser paints — no drop can land against the stale limit.
  const [runningUploadsCount, setRunningUploadsCount] = useState(0);
  const isMediaLibraryEnabled = useIsMediaLibraryEnabled();
  const { mediaUploader, mediaLibrary, fileSources } = useFileSource({
    page,
    onChange: onMediaUploaded,
    allowedFiles,
    maxFiles: maxCount - attachments.cards.length - runningUploadsCount,
    uploadSequence: 'parallel',
    sources,
  });

  useLayoutEffect(() => {
    setRunningUploadsCount(mediaUploader.runningUploads.length);
  }, [mediaUploader.runningUploads.length]);

  const imageEditor = useImageEditor();

  function onMediaUploaded([mediaLibraryItem]: MediaLibraryItem[]) {
    if (!mediaLibraryItem) return;
    const card =
      mediaLibraryItemToAssetCard(mediaLibraryItem, {
        enabled: isMediaLibraryEnabled,
      }) ?? AttachmentConvertor.fromMediaLibraryItem(mediaLibraryItem);
    addAttachments(card);
  }

  function addAttachments(
    attachments: AttachmentCards | AttachmentCard | AssetAttachmentCard,
  ) {
    const incoming = Array.isArray(attachments) ? attachments : [attachments];
    // Final capacity guard against the latest state: overlapping drops resolve
    // asynchronously and can each pass the same maxFiles check before either
    // registers. Only incoming cards are clamped, so a post that is already
    // over the cap never loses existing cards.
    onAttachmentsChange((prev) => ({
      cards: [
        ...prev.cards,
        ...incoming.slice(0, Math.max(0, maxCount - prev.cards.length)),
      ],
    }));
  }

  function removeAttachmentAtIndex(index: number) {
    onAttachmentsChange((prev) => ({
      cards: prev.cards.filter((a, i) => i !== index),
    }));
  }

  function moveAttachmentIndex(oldIndex: number, newIndex: number) {
    onAttachmentsChange((prev) => ({
      cards: ArrayUtils.move(prev.cards, oldIndex, newIndex),
    }));
  }

  function updateAttachmentMedium(newMedium: AttachmentCardMedium) {
    onAttachmentsChange((prev) => ({
      cards: prev.cards.map((attachment) =>
        attachment.medium?.id === newMedium?.id
          ? { ...attachment, medium: newMedium }
          : attachment,
      ),
    }));
  }

  return {
    addAttachments,
    updateAttachmentMedium,
    removeAttachmentAtIndex,
    attachments,
    moveAttachmentIndex,
    mediaUploader,
    mediaLibrary,
    fileSources,
    totalAttachmentsLength:
      attachments.cards.length + mediaUploader.runningUploads.length,
    ...imageEditor,
  };
}
